/**
 * galaxy-adjacency.ts — v4M6.4 selection-driven adjacency overlay.
 *
 * Replaces the v4M5.3b3c always-on hyperlane wireframe per `docs/MAP.md
 * §5`. Adjacency communicates on demand through three modes (chosen
 * by `drawGalaxyAdjacency` based on UI state, in priority order):
 *
 *  1. **Fleet-reachable.** Player has player-owned ships selected with
 *     a single source system + finite fleet range. Direct-hop reachable
 *     systems (adjacent and in range from the source) get brighter
 *     empire-coloured lines; multi-hop reachable systems (via BFS
 *     through `adjacentSystemIds` with the per-hop range gate) get
 *     fainter lines. The positive indicator replaces the v4M5.3b3e
 *     out-of-range star dimming — caller suppresses `outOfRange` while
 *     this mode is active.
 *
 *  2. **System-selected.** `ui.selection.kind === 'system'`. Short-dash
 *     empire-coloured lines from the selected system to each scouted
 *     adjacent system. Alpha fades with screen-space distance so far
 *     neighbours read as softer than close ones.
 *
 *  3. **Hover preview.** `ui.hoveredGalaxySystemId !== null` with no
 *     ship selection and no system selection. Looser dash pattern to
 *     distinguish from system-selected mode — the player can scrub the
 *     map to learn topology without committing a selection.
 *
 * All three modes FoW-gate: lines never extend to unscouted destinations
 * (the viewer empire's `scoutedSystemIds`). Warp Gate connections render
 * separately in `galaxy-view.ts` and are unaffected by this module —
 * they're player-built infrastructure with always-on visibility per
 * §5.3.
 *
 * No save-format change. Pure reader; no state mutation.
 */

import {
  type GameState,
  type StarSystem,
  type SystemId,
} from '../state/types/index';
import { hyperdriveRangeMultiplier } from '../game/research-effects';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';

// ---------------------------------------------------------------------------
// Source resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the selected fleet's source system + range for the
 * fleet-reachable mode. Mirrors `resolvePickerSource` in
 * `galaxy-jump-target.ts` but doesn't carry the picker empire — the
 * caller already has it. Returns null when the selection isn't a
 * single-source player-owned fleet.
 *
 * Duplicated here rather than imported because the modules have
 * different gates (the picker requires a hovered destination too);
 * pulling the helper into a shared module would require both modules
 * to import the new module — a wash on file count.
 */
export interface FleetSource {
  systemId: SystemId;
  range: number;
}

export function resolveFleetSource(state: GameState, ui: UiState): FleetSource | null {
  if (ui.selectedShipIds.size === 0) return null;
  const viewer = state.empires.get(viewerEmpireId(state, ui));
  if (!viewer) return null;
  let sourceSystemId: SystemId | null = null;
  let fleetRange = Number.POSITIVE_INFINITY;
  for (const id of ui.selectedShipIds) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    if (ship.ownerEmpireId !== viewer.id) return null;
    if (ship.systemId === null) continue;
    if (sourceSystemId === null) sourceSystemId = ship.systemId;
    else if (sourceSystemId !== ship.systemId) return null;
    const tmpl = state.shipTemplates.get(`${viewer.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    const r = tmpl.hyperdriveRange * hyperdriveRangeMultiplier(state, viewer);
    if (r < fleetRange) fleetRange = r;
  }
  if (sourceSystemId === null) return null;
  if (!Number.isFinite(fleetRange)) return null;
  return { systemId: sourceSystemId, range: fleetRange };
}

// ---------------------------------------------------------------------------
// Reachability
// ---------------------------------------------------------------------------

export interface ReachableSystems {
  /** Adjacent systems within fleetRange of `source` — single-hop reachable. */
  direct: Set<SystemId>;
  /** Systems reachable via 2+ hops, each step within fleetRange. Excludes `direct`. */
  multiHop: Set<SystemId>;
}

/**
 * BFS through `adjacentSystemIds` from `sourceSystemId`, gating each
 * step on the source-to-neighbour straight-line distance vs
 * `fleetRange`. The per-hop range gate matches the sim-side
 * `firstStepTowards` (v4M5.5) / `firstStepTowardsWeighted` (v4M8.2c)
 * helpers — what's reachable in the overlay is what the sim would
 * actually accept as a multi-hop dispatch path.
 *
 * `source` is excluded from both sets. Adjacent but out-of-range
 * systems are excluded from both sets (matches sim behaviour).
 */
export function getReachableSystemsFromSource(
  state: GameState,
  sourceSystemId: SystemId,
  fleetRange: number,
): ReachableSystems {
  const direct = new Set<SystemId>();
  const multiHop = new Set<SystemId>();
  if (fleetRange <= 0) return { direct, multiHop };
  const sourceSys = state.systems.get(sourceSystemId);
  if (!sourceSys) return { direct, multiHop };

  const visited = new Set<SystemId>([sourceSystemId]);
  // Queue carries (systemId, depth) so the first level (depth 1) goes
  // to `direct` and deeper levels go to `multiHop`.
  const queue: Array<{ id: SystemId; depth: number }> = [];

  for (const nId of sourceSys.adjacentSystemIds) {
    if (visited.has(nId)) continue;
    const n = state.systems.get(nId);
    if (!n) continue;
    if (hopInRange(sourceSys, n, fleetRange)) {
      visited.add(nId);
      direct.add(nId);
      queue.push({ id: nId, depth: 1 });
    }
  }

  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    const sys = state.systems.get(id);
    if (!sys) continue;
    for (const nId of sys.adjacentSystemIds) {
      if (visited.has(nId)) continue;
      const n = state.systems.get(nId);
      if (!n) continue;
      if (!hopInRange(sys, n, fleetRange)) continue;
      visited.add(nId);
      multiHop.add(nId);
      queue.push({ id: nId, depth: depth + 1 });
    }
  }

  return { direct, multiHop };
}

function hopInRange(a: StarSystem, b: StarSystem, fleetRange: number): boolean {
  return Math.hypot(b.x - a.x, b.y - a.y) <= fleetRange;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/**
 * Fleet-reachable lines from `source` to every direct + multi-hop
 * reachable system the viewer has scouted. Direct lines brighter
 * (alpha 0.7); multi-hop fainter (alpha 0.35). Empire-coloured.
 *
 * Unscouted destinations get no line — adjacency leaks ahead of the
 * scout, the §6 strict-FoW design intent flagged for v4M6.5 holds.
 */
function drawFleetReachable(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  source: FleetSource,
  color: string,
  scoutedSystemIds: ReadonlySet<SystemId>,
): void {
  const sourceSys = state.systems.get(source.systemId);
  if (!sourceSys) return;
  const reachable = getReachableSystemsFromSource(state, source.systemId, source.range);
  const sourceScreen = worldToScreen(ui.galaxyCamera, vp, {
    x: sourceSys.x,
    y: sourceSys.y,
  });

  ctx.save();
  ctx.lineWidth = 1.25;
  ctx.setLineDash([3, 3]);

  // Multi-hop first so direct lines layer on top (read brighter).
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = color;
  for (const destId of reachable.multiHop) {
    if (!scoutedSystemIds.has(destId)) continue;
    const dest = state.systems.get(destId);
    if (!dest) continue;
    const destScreen = worldToScreen(ui.galaxyCamera, vp, { x: dest.x, y: dest.y });
    ctx.beginPath();
    ctx.moveTo(sourceScreen.x, sourceScreen.y);
    ctx.lineTo(destScreen.x, destScreen.y);
    ctx.stroke();
  }

  ctx.globalAlpha = 0.7;
  for (const destId of reachable.direct) {
    if (!scoutedSystemIds.has(destId)) continue;
    const dest = state.systems.get(destId);
    if (!dest) continue;
    const destScreen = worldToScreen(ui.galaxyCamera, vp, { x: dest.x, y: dest.y });
    ctx.beginPath();
    ctx.moveTo(sourceScreen.x, sourceScreen.y);
    ctx.lineTo(destScreen.x, destScreen.y);
    ctx.stroke();
  }

  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * System-selected adjacency: lines from the selected system to each
 * scouted adjacent system. Alpha fades with screen-space distance
 * (close neighbours brighter, far ones softer) per `MAP.md §5.3`.
 * Short-dash + viewer-empire-coloured per §5.5 to distinguish from
 * Warp Gate lines.
 */
function drawSystemSelectionAdjacency(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  selectedSys: StarSystem,
  color: string,
  scoutedSystemIds: ReadonlySet<SystemId>,
): void {
  const selectedScreen = worldToScreen(ui.galaxyCamera, vp, {
    x: selectedSys.x,
    y: selectedSys.y,
  });

  // Walk adjacent systems; compute the max screen-space distance so
  // the alpha fade self-normalises rather than depending on the zoom
  // level. Close neighbours sit at alpha 0.75; far neighbours fade to
  // ~0.30.
  const neighbours: Array<{ sys: StarSystem; screen: { x: number; y: number }; screenDist: number }> = [];
  let maxDist = 0;
  for (const nId of selectedSys.adjacentSystemIds) {
    if (!scoutedSystemIds.has(nId)) continue;
    const n = state.systems.get(nId);
    if (!n) continue;
    const nScreen = worldToScreen(ui.galaxyCamera, vp, { x: n.x, y: n.y });
    const d = Math.hypot(nScreen.x - selectedScreen.x, nScreen.y - selectedScreen.y);
    if (d > maxDist) maxDist = d;
    neighbours.push({ sys: n, screen: nScreen, screenDist: d });
  }
  if (neighbours.length === 0) return;

  ctx.save();
  ctx.lineWidth = 1.25;
  ctx.setLineDash([3, 3]);
  ctx.strokeStyle = color;
  for (const { screen, screenDist } of neighbours) {
    const t = maxDist > 0 ? screenDist / maxDist : 0;
    ctx.globalAlpha = 0.75 - 0.45 * t;
    ctx.beginPath();
    ctx.moveTo(selectedScreen.x, selectedScreen.y);
    ctx.lineTo(screen.x, screen.y);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * Hover preview adjacency — looser dash so it reads as a transient
 * cue rather than a commitment. Same FoW gating + distance-fade as
 * system-selected mode. No-op when the hovered system is the same as
 * the system already selected (would just render the same lines twice).
 */
function drawHoverAdjacency(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  hoveredSys: StarSystem,
  color: string,
  scoutedSystemIds: ReadonlySet<SystemId>,
): void {
  const hoveredScreen = worldToScreen(ui.galaxyCamera, vp, {
    x: hoveredSys.x,
    y: hoveredSys.y,
  });
  ctx.save();
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 5]);
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.45;
  for (const nId of hoveredSys.adjacentSystemIds) {
    if (!scoutedSystemIds.has(nId)) continue;
    const n = state.systems.get(nId);
    if (!n) continue;
    const nScreen = worldToScreen(ui.galaxyCamera, vp, { x: n.x, y: n.y });
    ctx.beginPath();
    ctx.moveTo(hoveredScreen.x, hoveredScreen.y);
    ctx.lineTo(nScreen.x, nScreen.y);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Driver
// ---------------------------------------------------------------------------

/**
 * Returns true if the fleet-reachable overlay would render this frame.
 * Caller (galaxy-view.ts) uses this to short-circuit
 * `computeOutOfRangeSystemIds` — the reachable overlay's positive
 * indicator replaces the negative out-of-range fog per `MAP.md §5.3`.
 */
export function isFleetReachableModeActive(state: GameState, ui: UiState): boolean {
  return resolveFleetSource(state, ui) !== null;
}

/**
 * Top-level driver. Called once per frame from `galaxy-view.ts` after
 * the per-system loop but before ships + indicators. Picks the active
 * mode in priority order: fleet-reachable > system-selected > hover.
 * No-op when nothing applies (the default "no adjacency lines at all"
 * state per `MAP.md §5.3`).
 */
export function drawGalaxyAdjacency(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  if (ui.viewMode !== 'galaxy') return;
  const viewer = state.empires.get(viewerEmpireId(state, ui));
  if (!viewer) return;
  const color = viewer.color;
  const scoutedSystemIds = ui.devRevealAll
    ? null
    : viewer.scoutedSystemIds;
  // devRevealAll: treat every system as scouted by wrapping the empty
  // gate predicate. Branching once at the top keeps the per-line
  // hot path branch-free.
  const scoutedGate: ReadonlySet<SystemId> = scoutedSystemIds ?? new Set(
    [...state.systems.keys()],
  );

  const fleetSource = resolveFleetSource(state, ui);
  if (fleetSource !== null) {
    drawFleetReachable(ctx, state, ui, vp, fleetSource, color, scoutedGate);
    return;
  }

  if (ui.selection.kind === 'system') {
    const selectedSys = state.systems.get(ui.selection.id);
    if (selectedSys) {
      drawSystemSelectionAdjacency(ctx, state, ui, vp, selectedSys, color, scoutedGate);
    }
    return;
  }

  if (ui.hoveredGalaxySystemId !== null) {
    const hoveredSys = state.systems.get(ui.hoveredGalaxySystemId);
    if (hoveredSys) {
      drawHoverAdjacency(ctx, state, ui, vp, hoveredSys, color, scoutedGate);
    }
    return;
  }
}
